import React from 'react';
import { useState } from 'react';
import { useEffect } from 'react';
import { useRef } from 'react';
import { useMemo } from 'react';

import { JsonForms } from '@jsonforms/react';
import { materialRenderers, materialCells } from '@jsonforms/material-renderers';

//import Ajv from 'ajv';
//import addFormats from 'ajv-formats';
import { rankWith, schemaTypeIs, uiTypeIs, createAjv } from '@jsonforms/core';
import localize_ru from 'ajv-i18n/localize/ru'; // Импорт русской локализации

import { CustomTableLabelControl, CustomFixedcellLabelControl, ExternalLabelControl } from './CustomLabelRenderer';
import CustomNumberControl from './CustomNumberRenderer';
import CustomIntegerControl from './CustomIntegerRenderer';
import CustomGroupRenderer from './CustomGroupRenderer';
import CustomLayoutRenderer from './CustomLayoutRenderer';

import './FormRenderer.css';


// локализация дейтпикера в JsonForms
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
dayjs.locale("ru");

/*
const options = {meta: true,}

const ajv = new Ajv({
  allErrors: true,
  verbose: true,
  strict: false,
  ...options,
});
addFormats(ajv);
*/

const ajvOptions = {  
  allErrors: true,
  verbose: true,
  strict: false,
}

const ajv = createAjv(ajvOptions)
require('ajv-keywords')(ajv);
require('ajv-errors')(ajv);


// Кастомный AJV с локализацией
const customAjv = {
  ...ajv,
  compile: (schema) => {
    // Компилируем схему с помощью стандартного AJV
    const validate = ajv.compile(schema);
    //console.log(validate)
    
    // Создаём кастомную функцию валидации
    const customValidate = (data) => {
      // Выполняем валидацию
      const valid = validate(data);
      // Если есть ошибки, локализуем их
      if (!valid && validate.errors) {
        // Применяем русскую локализацию
        localize_ru(validate.errors);
        // Корректируем кастомные сообщения из errorMessage
        //validate.errors.forEach((error) => {
        //  if (error.keyword === 'errorMessage' && error.params && error.params.errors) {
        //    error.message = error.params.errors[0].message;
        //  }
        //});
      }
      return valid;
    };

    Object.defineProperties(customValidate, {
      ...Object.getOwnPropertyDescriptors(validate),
      // Убедимся, что errors остаётся геттером
      errors: {
        get: () => validate.errors,
        enumerable: true,
      },
      // Сохраняем schema
      schema: {
        value: validate.schema,
        enumerable: true,
        writable: true,
      },
    });

    return customValidate;
  },
};


const numberSliderTester = rankWith(
  3, // Приоритет рендерера
  schemaTypeIs('number') // Условие: рендерить, если тип number
);
const integerSliderTester = rankWith(
  3, // Приоритет рендерера
  schemaTypeIs('integer') // Условие: рендерить, если тип integer
);
const groupTester = rankWith(
  3, 
  uiTypeIs('Group')
);
const horizontalLayoutTester = rankWith(
  3, 
  uiTypeIs('HorizontalLayout')
);

// Кастомные рендеры для отдельных типов полей
const renderers = [
  ...materialRenderers,
  //register custom renderers
  {
    tester: (uischema) => {
      return uischema.options && uischema.options.labelRenderer === 'fixed_cell' ? 100 : -1;
    },
    renderer: CustomFixedcellLabelControl,
  },
  {
    tester: (uischema) => {
      return uischema.options && uischema.options.labelRenderer === 'table_head' ? 100 : -1;
    },
    renderer: CustomTableLabelControl,
  },

  // Renderer for labels inside Groups (not inside Layouts)
  {
    tester: (uischema) => {
      return uischema.options && uischema.options.labelRenderer === 'external' ? 100 : -1;
    },
    renderer: ExternalLabelControl,
  }, 


  {
    tester: numberSliderTester,
    renderer: CustomNumberControl,
  },
  {
    tester: integerSliderTester,
    renderer: CustomIntegerControl,
  },
  { 
    tester: groupTester, 
    renderer: CustomGroupRenderer, 
  },
  { 
    tester: horizontalLayoutTester, 
    renderer: CustomLayoutRenderer, 
  },                
];

export { renderers };


export default function FormRenderer ({
  schema,  
  uiSchema,
  validationMode,
  formViewMode, 
  
  samplesDict = undefined,
  taskData = {},
  handleFormChange = () => () => {},
  readonly = false
}){

  const createTranslator = (locale) => (key, defaultMessage) => {
    //console.log(`Locale: ${locale}, Key: ${key}, Default Message: ${defaultMessage}`);
    //console.log(key)

    if(String(key) === "error.required"){
      return "Обязательное поле"
    } 

    //if(String(key) == "row_02.code_analizator2.error.custom"){
    //  return "должно быть"
    //}

    return defaultMessage;
  };
  
  const locale = 'ru'
  const translation = useMemo(() => createTranslator(locale), [locale]);


  // form scrolling logic
  const formContainerRef = useRef(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);


  // Мемоизация обработки uiSchema для предотвращения лишних вычислений
  const newUiSchema = useMemo(() => {
    if (!uiSchema || !uiSchema.elements) {
      return uiSchema;
    }

    let processedUiSchema = JSON.parse(JSON.stringify(uiSchema))

    for (let group in processedUiSchema.elements) {

      //////////// HORIZONTAL SCROLL MODE ///////////

      // Group labels
      if (formViewMode === 'HORIZONTAL_SCROLL') {
       
        if (processedUiSchema.elements[group].options) {
          processedUiSchema.elements[group].options.width = { sm: '60vw', md: '65vw', lg: '70vw', xl: '74vw' }
        } else {
          processedUiSchema.elements[group].options = {"width": { sm: '60vw', md: '65vw', lg: '70vw', xl: '74vw' }}
        }
      }
      ////////////////////////////////////////////////


      for (let groupElement in processedUiSchema.elements[group].elements) {

        // Insert variables values into Labels
        for (let element in processedUiSchema.elements[group].elements[groupElement].elements) {
          let labelText = processedUiSchema.elements[group].elements[groupElement].elements[element].text

          let literalValueInText = null;
          const regex = /\{([^}]*)\}/;
          if (regex.test(labelText)) {
            // Извлекаем значение из первой найденной пары скобок
            const match = labelText.match(regex);
            literalValueInText = match[1]; // Значение внутри скобок
          }


          if (samplesDict) {
            if (literalValueInText in samplesDict) {
              labelText = labelText.replace(/\{([^}]*)\}/g, samplesDict[literalValueInText]);
              processedUiSchema.elements[group].elements[groupElement].elements[element].text = labelText
            }
          }
        } 

        //////////// HORIZONTAL SCROLL MODE ///////////
        if (formViewMode === 'HORIZONTAL_SCROLL') {

          // For Labels inside Groups (not inside Layouts)
          if (processedUiSchema.elements[group].elements[groupElement].type === 'Label') {

            if (processedUiSchema.elements[group].elements[groupElement].options) {
              processedUiSchema.elements[group].elements[groupElement].options.width = { sm: '60vw', md: '65vw', lg: '70vw', xl: '72vw' }
            }  else {
              processedUiSchema.elements[group].elements[groupElement].options = {"width": { sm: '60vw', md: '65vw', lg: '70vw', xl: '72vw' }}
            }
          }

          // Horizontal Layouts
          if (processedUiSchema.elements[group].elements[groupElement].type === 'HorizontalLayout') {

            // Horizontal Layouts with elements quantity <= 2
            if (processedUiSchema.elements[group].elements[groupElement].elements.length <= 2) {

              if (processedUiSchema.elements[group].elements[groupElement].options) {
                processedUiSchema.elements[group].elements[groupElement].options.width = { sm: '60vw', md: '65vw', lg: '70vw', xl: '70vw' }
              }  else {
                processedUiSchema.elements[group].elements[groupElement].options = {"width": { sm: '60vw', md: '65vw', lg: '70vw', xl: '70vw' }}
              }

            // Horizontal Layouts with elements quantity > 2
            } else {
              const layoutLength = processedUiSchema.elements[group].elements[groupElement].elements.length
              const elementWidth = 200
              if (processedUiSchema.elements[group].elements[groupElement].options) {
                processedUiSchema.elements[group].elements[groupElement].options.width = layoutLength * (elementWidth + 16) - 16
              }  else {
                processedUiSchema.elements[group].elements[groupElement].options = {"width": (layoutLength * (elementWidth + 16) - 16 )}
              }

              /*
              for (let element in processedUiSchema.elements[group].elements[groupElement].elements) {
                if (processedUiSchema.elements[group].elements[groupElement].elements[element].options) {
                  processedUiSchema.elements[group].elements[groupElement].elements[element].options.width = 200
                }  else {
                  processedUiSchema.elements[group].elements[groupElement].elements[element].options = {"width": 200}
                }
              }
              */
            }
              
          }
        }
        ///////////////////////////////////////////////
      }  
    }

    return processedUiSchema;
  }, [uiSchema, formViewMode, samplesDict]);
  

  // Form horizontal scroll Effect
  useEffect(() => {
    const checkOverflow = () => {
      //alert('checking overflow')
      const container = formContainerRef.current;
      if (container) {
        const overflow = container.scrollWidth > container.clientWidth;
        setHasOverflow(overflow);
        updateScrollButtons(container);
      }
    };

    const updateScrollButtons = (container) => {
      setCanScrollLeft(container.scrollLeft > 0);
      setCanScrollRight(container.scrollLeft + container.clientWidth < container.scrollWidth);
    };

    const handleScroll = () => {
      //alert('scroll')
      updateScrollButtons(formContainerRef.current);
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    //alert('window resize listener added');

    const container = formContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      //alert('scroll listener added');
    }

    return () => {
      window.removeEventListener('resize', checkOverflow);
      //alert('window resize listener removed');

      if (container) {
        container.removeEventListener('scroll', handleScroll);
        //alert('scroll listener removed');
      }
    };

  }, [newUiSchema]); // newUiSchema теперь мемоизирован, поэтому это безопасно


  // Плавная прокрутка с использованием requestAnimationFrame
  const smoothScroll = (element, targetPosition, duration = 300) => {
    const startPosition = element.scrollLeft;
    const distance = targetPosition - startPosition;
    let startTime = null;

    const animation = (currentTime) => {
      if (startTime === null) startTime = currentTime;
      const timeElapsed = currentTime - startTime;
      const progress = Math.min(timeElapsed / duration, 1);
      
      // Используем easing функцию для более плавной анимации
      const ease = progress < 0.5 
        ? 2 * progress * progress 
        : 1 - Math.pow(-2 * progress + 2, 2) / 2;
      
      element.scrollLeft = startPosition + distance * ease;
      
      if (progress < 1) {
        requestAnimationFrame(animation);
      }
    };

    requestAnimationFrame(animation);
  };

  const scrollLeft = () => {
    if (formContainerRef.current) {
      const container = formContainerRef.current;
      const scrollAmount = 432;
      const targetPosition = Math.max(0, container.scrollLeft - scrollAmount);
      smoothScroll(container, targetPosition);
    }
  };

  const scrollRight = () => {
    if (formContainerRef.current) {
      const container = formContainerRef.current;
      const scrollAmount = 432;
      const maxScroll = container.scrollWidth - container.clientWidth;
      const targetPosition = Math.min(maxScroll, container.scrollLeft + scrollAmount);
      smoothScroll(container, targetPosition);
    }
  };

  return (
    <>
      <div className="d-flex flex-row">
        {hasOverflow && (
          canScrollLeft ? (
            <div className="form-scroll-button scroll-button-active scroll-button-left" onClick={scrollLeft}>
            </div>
          ) : (
            <div className="form-scroll-button scroll-button-inactive scroll-button-left">
            </div>                      
          )
        )}                    
        <div 
          id="form-content"
          ref={formContainerRef}                 
          className={ formViewMode === 'HORIZONTAL_SCROLL' ? 'form-scrollable-content' : '' }
          //style={formContainerStyles}
          //style={{ overflow: 'scroll', width: '100%', display: 'flex', position: 'relative'}}                    
          //onMouseDown={handleMouseDown}
          //onMouseUp={stopDragging}
          //onDragStart={(event) => event.preventDefault()}
        >
          <JsonForms
            schema={schema}
            uischema={newUiSchema}
            data={taskData}
            renderers={renderers}
            cells={materialCells}
            onChange={handleFormChange}
            validationMode={validationMode}
            ajv={customAjv}
            i18n={{locale: locale, translate: translation}}
            readonly={readonly}
            //additionalErrors={errors}
          />
        </div>
        {hasOverflow && (
          canScrollRight ? (
            <div className="form-scroll-button scroll-button-active scroll-button-right" onClick={scrollRight}>
            </div>
          ) : (
            <div className="form-scroll-button scroll-button-inactive scroll-button-right">
            </div>                      
          )
        )}                       
      </div>
    </> 
  );
}





